import apiService from '../apiService';

/**
 * 取得會員積分列表
 * @param {Object} params - 查詢參數
 * @param {number} params.page - 頁碼
 * @param {number} params.per_page - 每頁筆數
 * @param {string} params.point_type_name - 積分類型名稱（如 GP）用於數值區間篩選
 * @param {string} params.point_type - 積分類型代碼（如 CP、PP）用於數值區間篩選，與 point_type_name 擇一或同送
 * @param {number} params.point_type_min - 該類型積分最小值（含邊界，含 0 若在區間內）
 * @param {number} params.point_type_max - 該類型積分最大值（含邊界）
 * @returns {Promise} API 響應
 */
export const getUserPoints = async (params = {}) => {
    const url = '/api/admin/user-points?include_all_types=true';
    return apiService.GET(url, params);
};

/**
 * 新增或更新會員積分
 * @param {Object} data - 積分資料
 * @returns {Promise} API 響應
 */
export const createOrUpdateUserPoints = async (data) => {
    const url = '/api/admin/user-points';
    return apiService.POST(url, data);
};

/**
 * 更新積分記錄
 * @param {number} id - 積分記錄 ID
 * @param {Object} data - 要更新的資料
 * @returns {Promise} API 響應
 */
export const updateUserPoints = async (id, data) => {
    const url = `/api/admin/user-points/${id}`;
    return apiService.PUT(url, data);
};

/**
 * 刪除積分記錄
 * @param {number} id - 積分記錄 ID
 * @returns {Promise} API 響應
 */
export const deleteUserPoints = async (id) => {
    const url = `/api/admin/user-points/${id}`;
    return apiService.DELETE(url);
};

/**
 * 取得積分異動記錄查詢
 * @param {Object} params - 查詢參數
 * @param {number} params.user_id - 用戶 ID (可選)
 * @param {number} params.page - 頁碼
 * @param {number} params.per_page - 每頁筆數
 * @param {string} params.point_type - 積分類型 (可選)
 * @returns {Promise} API 響應
 */
export const getPointLogs = async (params = {}) => {
    const url = '/api/admin/logs/point';
    return apiService.GET(url, params);
};

/**
 * 取得指定用戶的所有積分餘額
 * @param {number} userId - 用戶 ID
 * @returns {Promise} API 響應
 */
export const getUserPointsStatistics = async (userId) => {
    const url = `/api/admin/users/${userId}/points`;
    return apiService.GET(url);
};

/**
 * 調整用戶積分 (增加或減少)
 * @param {number} userId - 用戶 ID
 * @param {Object} data - 調整資料
 * @param {number} data.points - 積分數量
 * @param {string} data.reason - 調整原因
 * @param {string} data.type - 調整類型 (add/subtract)
 * @returns {Promise} API 響應
 */
export const adjustUserPoints = async (userId, data) => {
    const url = `/api/admin/users/${userId}/points/adjust`;
    return apiService.POST(url, data);
};

// ========== 積分類型管理 ==========

/**
 * 獲取積分類型列表
 * @param {Object} params - 查詢參數
 * @param {number} params.page - 頁碼
 * @param {number} params.per_page - 每頁筆數
 * @returns {Promise} API 響應
 */
export const getPointTypes = async (params = {}) => {
    const url = '/api/admin/point-types';
    return apiService.GET(url, params);
};

/**
 * 創建新積分類型
 * @param {Object} pointTypeData - 積分類型資料
 * @param {string} pointTypeData.name - 積分類型名稱
 * @param {string} pointTypeData.description - 描述
 * @returns {Promise} API 響應
 */
export const createPointType = async (pointTypeData) => {
    const url = '/api/admin/point-types';
    return apiService.POST(url, pointTypeData);
};

/**
 * 獲取積分類型選項
 * @returns {Promise} API 響應
 */
export const getPointTypeOptions = async () => {
    const url = '/api/admin/point-types/options';
    return apiService.GET(url);
};

/**
 * 獲取單一積分類型詳情
 * @param {number} pointTypeId - 積分類型 ID
 * @returns {Promise} API 響應
 */
export const getPointTypeDetail = async (pointTypeId) => {
    const url = `/api/admin/point-types/${pointTypeId}`;
    return apiService.GET(url);
};

/**
 * 更新積分類型
 * @param {number} pointTypeId - 積分類型 ID
 * @param {Object} pointTypeData - 要更新的積分類型資料
 * @returns {Promise} API 響應
 */
export const updatePointType = async (pointTypeId, pointTypeData) => {
    const url = `/api/admin/point-types/${pointTypeId}`;
    return apiService.PUT(url, pointTypeData);
};

/**
 * 刪除積分類型
 * @param {number} pointTypeId - 積分類型 ID
 * @returns {Promise} API 響應
 */
export const deletePointType = async (pointTypeId) => {
    const url = `/api/admin/point-types/${pointTypeId}`;
    return apiService.DELETE(url);
};

/**
 * 切換積分類型狀態
 * @param {number} pointTypeId - 積分類型 ID
 * @returns {Promise} API 響應
 */
export const togglePointTypeStatus = async (pointTypeId) => {
    const url = `/api/admin/point-types/${pointTypeId}/toggle-status`;
    return apiService.PATCH(url);
};
